"use client";

import { useInView } from "@/hooks/use-in-view";
import { useEffect, useRef, useState } from "react";

/** How far each column starts rotated (degrees) before springing to rest. */
export const SCROLL_COLUMN_REVEAL_ENTER_DEG = 16;
/**
 * Release-style spring: loose enough to overshoot, then settle.
 * Drives rotation only — opacity uses a separate ease-out.
 */
export const SCROLL_COLUMN_REVEAL_SPRING_STIFFNESS = 0.16;
export const SCROLL_COLUMN_REVEAL_SPRING_DAMPING = 0.78;
/** Opacity fade duration (cubic ease-out, no spring). */
export const SCROLL_COLUMN_REVEAL_OPACITY_EASE_MS = 280;
/**
 * Bottom-only inset: the trigger is a line near the lower viewport.
 * Scroll down past it → animate in and STAY in while the section sits at the
 * top of the screen. Scroll back up past that same line → animate out.
 * (Do not inset the top — that wrongly untriggers when content reaches the top.)
 */
export const SCROLL_COLUMN_REVEAL_ROOT_MARGIN = "0px 0px -35% 0px";
export const SCROLL_COLUMN_REVEAL_THRESHOLD = 0;

/** Advance a 1D spring one frame (SlideToggle release-style). */
export function stepScrollColumnRevealSpring(
  position: number,
  velocity: number,
  target: number,
  stiffness = SCROLL_COLUMN_REVEAL_SPRING_STIFFNESS,
  damping = SCROLL_COLUMN_REVEAL_SPRING_DAMPING
): { position: number; velocity: number } {
  const nextVelocity = (velocity + (target - position) * stiffness) * damping;
  const nextPosition = position + nextVelocity;
  if (Math.abs(nextPosition - target) < 0.001 && Math.abs(nextVelocity) < 0.001) {
    return { position: target, velocity: 0 };
  }
  return { position: nextPosition, velocity: nextVelocity };
}

/** Cubic ease-out for opacity (no overshoot). */
export function easeOutCubic(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return 1 - (1 - x) ** 3;
}

/** Time-based ease-out opacity from `from` → `to`. */
export function easeOutOpacity(
  elapsedMs: number,
  from: number,
  to: number,
  durationMs = SCROLL_COLUMN_REVEAL_OPACITY_EASE_MS
): number {
  const t = easeOutCubic(durationMs <= 0 ? 1 : elapsedMs / durationMs);
  return Number((from + (to - from) * t).toFixed(3));
}

/** Left column: hinged on bottom-right, swings in from the left. */
export function scrollColumnLeftRotateDeg(
  progress: number,
  enterDeg = SCROLL_COLUMN_REVEAL_ENTER_DEG
): number {
  // Avoid `-0` from `(1 - 1) * -enterDeg` so style/tests compare cleanly.
  return progress === 1 ? 0 : (1 - progress) * -enterDeg;
}

/** Right column: hinged on bottom-left, swings in from the right. */
export function scrollColumnRightRotateDeg(
  progress: number,
  enterDeg = SCROLL_COLUMN_REVEAL_ENTER_DEG
): number {
  return progress === 1 ? 0 : (1 - progress) * enterDeg;
}

export interface ScrollColumnReveal {
  ref: React.Ref<HTMLDivElement>;
  inView: boolean;
  leftRotate: number;
  rightRotate: number;
  opacity: number;
}

/**
 * Bidirectional scroll reveal for two-column marketing blocks:
 * spring rotate in/out + ease-out opacity, shared trigger band.
 *
 * Animation is driven by a single `inView` effect (tick defined inline) so
 * React Strict Mode effect re-runs always restart cleanly.
 */
export function useScrollColumnReveal(): ScrollColumnReveal {
  const { ref, inView } = useInView<HTMLDivElement>({
    rootMargin: SCROLL_COLUMN_REVEAL_ROOT_MARGIN,
    threshold: SCROLL_COLUMN_REVEAL_THRESHOLD,
    once: false,
  });

  const [progress, setProgress] = useState(0);
  const [opacity, setOpacity] = useState(0);
  const progressRef = useRef(0);
  const velocityRef = useRef(0);
  const opacityRef = useRef(0);
  const publishedProgressRef = useRef(0);
  const publishedOpacityRef = useRef(0);
  const reducedMotionRef = useRef(false);

  useEffect(() => {
    reducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    const target = inView ? 1 : 0;
    const opacityFrom = opacityRef.current;
    const opacityTo = target;
    const opacityStartMs = performance.now();

    const publish = (nextProgress: number, nextOpacity: number, force = false) => {
      // Coalesce React commits: ~2dp progress / 3dp opacity is visually enough
      // and avoids a setState storm on low-end Chromebooks.
      const pubP = Math.round(nextProgress * 100) / 100;
      const pubO = nextOpacity;
      if (!force && pubP === publishedProgressRef.current && pubO === publishedOpacityRef.current) {
        return;
      }
      publishedProgressRef.current = pubP;
      publishedOpacityRef.current = pubO;
      setProgress(pubP);
      setOpacity(pubO);
    };

    if (reducedMotionRef.current) {
      progressRef.current = target;
      velocityRef.current = 0;
      opacityRef.current = opacityTo;
      publish(target, opacityTo, true);
      return;
    }

    let rafId: number | null = null;

    const tick = () => {
      const next = stepScrollColumnRevealSpring(progressRef.current, velocityRef.current, target);
      progressRef.current = next.position;
      velocityRef.current = next.velocity;

      const nextOpacity = easeOutOpacity(
        performance.now() - opacityStartMs,
        opacityFrom,
        opacityTo
      );
      opacityRef.current = nextOpacity;
      publish(next.position, nextOpacity);

      const springDone = next.velocity === 0 && Math.abs(next.position - target) <= 0.001;
      const opacityDone = Math.abs(nextOpacity - opacityTo) <= 0.001;

      if (!springDone || !opacityDone) {
        rafId = requestAnimationFrame(tick);
      } else {
        progressRef.current = target;
        velocityRef.current = 0;
        opacityRef.current = opacityTo;
        publish(target, opacityTo, true);
        rafId = null;
      }
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [inView]);

  return {
    ref,
    inView,
    leftRotate: scrollColumnLeftRotateDeg(progress),
    rightRotate: scrollColumnRightRotateDeg(progress),
    opacity,
  };
}
